import React, {useMemo} from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {findValidOutStatuses, isTerminalStatus, sortTaskStatuses} from 'processes/status/TaskStatusUtil';

import {ProcessTaskStatusLifecycleGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';
import {isProcessTaskStatus} from '../ProcessConstants';
import ProcessTaskStatusDisplay from './ProcessTaskStatusDisplay';

interface ProcessTaskStatusSelectorProps {
  typedValue: TypedValue | null;
  lastSyncedUpstreamValue?: TypedValue | null;
  statusLifecycle?: ProcessTaskStatusLifecycleGraphQL;
  onUpdate: (tv: TypedValue) => void;
  locked?: boolean;
}

interface ProcessTaskStatusSelectorContentProps {
  typedValue: TypedValue | null;
  lastSyncedUpstreamValue?: TypedValue | null;
  statusLifecycle: ProcessTaskStatusLifecycleGraphQL;
  onUpdate: (tv: TypedValue) => void;
  locked?: boolean;
}

function findStatusFromName(
  name: string,
  statusLifecycle: ProcessTaskStatusLifecycleGraphQL
): ProcessTaskStatus | undefined {
  // eslint-disable-next-line no-restricted-syntax
  return _.flatten(
    statusLifecycle.transitions.map((transition) => [transition.inStatus, transition.outStatus])
  ).find((status) => status.displayName === name);
}

function ProcessTaskStatusSelectorContent(props: ProcessTaskStatusSelectorContentProps): JSX.Element {
  const startingStatusTypedValue = props.lastSyncedUpstreamValue ?? props.typedValue;
  let startingStatus: ProcessTaskStatus =
    startingStatusTypedValue?.type === BaseTypedValueType.TASK_STATUS
      ? {
          __typename: 'ProcessTaskStatusNode' as 'ProcessTaskStatusNode',
          id: startingStatusTypedValue.id,
          displayName: startingStatusTypedValue.displayName,
          statusType: startingStatusTypedValue.statusType as ProcessTaskStatusType,
        }
      : startingStatusTypedValue
        ? findStatusFromName(formatTypedValue(startingStatusTypedValue), props.statusLifecycle)!
        : props.statusLifecycle.initialStatus;
  if (startingStatus == null) {
    startingStatus = props.statusLifecycle.initialStatus;
  }
  assertAndContinue(startingStatus != null, 'Task status should not be null');

  const validStatuses = useMemo(
    () => sortTaskStatuses(findValidOutStatuses(startingStatus, props.statusLifecycle)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.statusLifecycle, props.lastSyncedUpstreamValue]
  );
  const options = validStatuses.map((status) => ({
    node: (
      <ProcessTaskStatusDisplay
        statusType={status.statusType}
        displayName={status.displayName}
        showTerminalStatusTooltip={false}
      />
    ),
    onUpdateKey: status.id,
  }));

  const displayStatus =
    props.typedValue?.type === BaseTypedValueType.TASK_STATUS &&
    isProcessTaskStatus(props.typedValue?.statusType)
      ? props.typedValue
      : startingStatus;
  const staticDisplay = (
    <ProcessTaskStatusDisplay
      statusType={displayStatus.statusType as ProcessTaskStatusType}
      displayName={displayStatus.displayName}
      showTerminalStatusTooltip={!props.locked && isTerminalStatus(startingStatus.id, props.statusLifecycle)}
    />
  );

  if (props.locked || options.length === 0) {
    return staticDisplay;
  }

  return (
    <div className="u-position-relative">
      <OptionCellDropdown
        locked={false}
        isSelected={true}
        options={options}
        onUpdate={(newStatusId) => {
          const newStatus = validStatuses.find((status) => status.id === newStatusId);
          assertAndContinue(newStatus != null, `Invalid status selected: ${newStatusId}`);
          props.onUpdate({
            type: BaseTypedValueType.TASK_STATUS,
            id: newStatus!.id,
            displayName: newStatus!.displayName,
            statusType: newStatus!.statusType,
          });
        }}
        staticDisplay={staticDisplay}
        fullWidth={true}
        lightHoverBackground={true}
      />
    </div>
  );
}

export default function ProcessTaskStatusSelector(props: ProcessTaskStatusSelectorProps): JSX.Element | null {
  if (props.statusLifecycle == null) {
    if (props.typedValue?.type === BaseTypedValueType.TASK_STATUS) {
      return (
        <ProcessTaskStatusDisplay
          statusType={props.typedValue.statusType as ProcessTaskStatusType}
          displayName={props.typedValue.displayName}
          showTerminalStatusTooltip={false}
        />
      );
    } else {
      return null;
    }
  }
  return <ProcessTaskStatusSelectorContent {...props} statusLifecycle={props.statusLifecycle} />;
}
